import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Circle, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import GoogleTileLayer from "./GoogleTileLayer";
import { feetToMeters } from "../../lib/utils";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const createNumberedIcon = (number, color = "#0F172A") => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background-color: ${color};
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

const guardIcon = L.divIcon({
  className: "guard-marker",
  html: `<div style="
    background-color: #F97316;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 0 10px rgba(249, 115, 22, 0.6);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      if (onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

const isValidPoint = (p) =>
  p != null && p.latitude != null && p.longitude != null && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude));

// Leaflet guarda el tamaño del contenedor al crearse; si el mapa nace oculto o sin layout (0px),
// fitBounds calcula un zoom NaN y lanza "Invalid LatLng object: (NaN, NaN)". Este componente
// recalcula el tamaño cuando el contenedor cambia y avisa para reajustar la vista.
const SizeWatcher = ({ onResize }) => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (!container.clientWidth || !container.clientHeight) return;
      map.invalidateSize({ pan: false });
      onResize?.();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map, onResize]);

  return null;
};

const FitBounds = ({ checkpoints }) => {
  const map = useMap();
  const [sizeTick, setSizeTick] = useState(0);
  const fittedRef = useRef(false);
  const handleResize = useRef(() => setSizeTick((n) => n + 1)).current;

  useEffect(() => {
    fittedRef.current = false;
  }, [checkpoints]);

  useEffect(() => {
    const points = (checkpoints || []).filter(isValidPoint);
    if (!points.length || !map || fittedRef.current) return;
    try {
      map.invalidateSize({ pan: false });
      const size = map.getSize();
      if (!size.x || !size.y) return; // se reintenta cuando el contenedor tenga tamaño
      const pad = Math.max(0, Math.min(50, Math.floor(Math.min(size.x, size.y) / 4)));
      const bounds = L.latLngBounds(points.map((cp) => [Number(cp.latitude), Number(cp.longitude)]));
      map.fitBounds(bounds, { padding: [pad, pad], maxZoom: 18 });
      fittedRef.current = true;
    } catch (error) {
      console.error("Error ajustando bounds del mapa:", error);
    }
  }, [checkpoints, map, sizeTick]);

  return <SizeWatcher onResize={handleResize} />;
};

const FocusOnSelectedCheckpoint = ({ selectedCheckpoint, checkpoints }) => {
  const map = useMap();
  
  useEffect(() => {
    if (isValidPoint(selectedCheckpoint) && map) {
      try {
        if (!map.getSize().x) return;
        const checkpointLatLng = L.latLng(selectedCheckpoint.latitude, selectedCheckpoint.longitude);
        const bounds = map.getBounds();
        
        // Verificar que el mapa esté listo
        if (bounds && map.getContainer() && map.getContainer().offsetParent !== null) {
          // Verificar si el checkpoint está visible en el viewport actual
          const isVisible = bounds.contains(checkpointLatLng);
          
          if (!isVisible) {
            // Si no está visible, centrar el mapa en el checkpoint con zoom 15
            map.setView(checkpointLatLng, 15, { animate: true });
          }
        }
      } catch (error) {
        console.error("Error enfocando checkpoint:", error);
      }
    }
  }, [selectedCheckpoint, map]);
  
  return null;
};

const CenterMap = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && map && Array.isArray(center) && center.length === 2) {
      try {
        if (center.every(Number.isFinite) && map.getSize().x) {
          map.setView(center, map.getZoom() ?? 15, { animate: false });
        }
      } catch (error) {
        console.error("Error centrando mapa:", error);
      }
    }
  }, [center, map]);
  
  return null;
};

export const CheckpointMap = ({
  checkpoints: rawCheckpoints = [],
  selectedCheckpoint,
  onCheckpointClick,
  onMapClick,
  guardPosition,
  showRadius = true,
  height = "400px",
  interactive = true,
  completedCheckpoints = [],
}) => {
  const [mapCenter, setMapCenter] = useState([18.478973, -69.967669]); // República Dominicana default
  const [userLocation, setUserLocation] = useState(null);
  const mapRef = useRef(null);
  const checkpoints = useMemo(() => rawCheckpoints.filter(isValidPoint), [rawCheckpoints]);

  // Obtener ubicación actual del usuario al montar el componente
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setUserLocation({ latitude: lat, longitude: lng });
        },
        (error) => {
          // Si falla la geolocalización, intentar obtener ubicación aproximada por IP
          fetch("https://ipapi.co/json/")
            .then((response) => response.json())
            .then((data) => {
              if (data.latitude && data.longitude) {
                setUserLocation({
                  latitude: parseFloat(data.latitude),
                  longitude: parseFloat(data.longitude),
                });
              }
            })
            .catch((err) => {
              console.error("Error obteniendo ubicación por IP:", err);
            });
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    } else {
      // Si no hay geolocalización, intentar obtener ubicación por IP
      fetch("https://ipapi.co/json/")
        .then((response) => response.json())
        .then((data) => {
          if (data.latitude && data.longitude) {
            setUserLocation({
              latitude: parseFloat(data.latitude),
              longitude: parseFloat(data.longitude),
            });
          }
        })
        .catch((err) => {
          console.error("Error obteniendo ubicación por IP:", err);
        });
    }
  }, []);

  useEffect(() => {
    // Solo actualizar el centro si no hay checkpoints, para evitar conflictos con FitBounds
    if (checkpoints.length === 0) {
      if (isValidPoint(guardPosition)) {
        setMapCenter([guardPosition.latitude, guardPosition.longitude]);
      } else if (userLocation) {
        setMapCenter([userLocation.latitude, userLocation.longitude]);
      }
    }
  }, [checkpoints.length, guardPosition, userLocation]);

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height }}>
      <MapContainer
        center={mapCenter}
        zoom={15}
        style={{ height: "100%", width: "100%" }}
        ref={mapRef}
        scrollWheelZoom={interactive}
        dragging={interactive}
        zoomControl={interactive}
      >
        <GoogleTileLayer />
        
        {interactive && onMapClick && <MapClickHandler onMapClick={onMapClick} />}
        {checkpoints.length > 0 && <FitBounds checkpoints={checkpoints} />}
        {checkpoints.length === 0 && <SizeWatcher />}
        {checkpoints.length === 0 && (guardPosition || userLocation) && (
          <CenterMap center={mapCenter} />
        )}
        {selectedCheckpoint && <FocusOnSelectedCheckpoint selectedCheckpoint={selectedCheckpoint} checkpoints={checkpoints} />}
        
        {checkpoints.map((checkpoint, index) => {
          const isCompleted = completedCheckpoints.includes(checkpoint.id);
          const isSelected = selectedCheckpoint?.id === checkpoint.id;
          
          // Determinar el color: verde si está completado, naranja si está seleccionado, negro por defecto
          let markerColor = "#0F172A"; // Negro por defecto
          if (isCompleted) {
            markerColor = "#10B981"; // Verde para completados
          } else if (isSelected) {
            markerColor = "#F97316"; // Naranja para seleccionado
          }
          
          return (
            <div key={checkpoint.id}>
              {showRadius && (
                <Circle
                  center={[checkpoint.latitude, checkpoint.longitude]}
                  radius={feetToMeters(checkpoint.radius_feet)}
                  pathOptions={{
                    color: markerColor,
                    fillColor: markerColor,
                    fillOpacity: 0.1,
                    weight: 2,
                  }}
                />
              )}
              <Marker
                position={[checkpoint.latitude, checkpoint.longitude]}
                icon={createNumberedIcon(
                  index + 1,
                  markerColor
                )}
                eventHandlers={{
                  click: () => onCheckpointClick && onCheckpointClick(checkpoint),
                }}
              >
                <Popup>
                  <div className="p-2">
                    <h3 className="font-bold text-slate-900">{checkpoint.name}</h3>
                    <p className="text-sm text-slate-600">{checkpoint.description}</p>
                    {isCompleted && (
                      <p className="text-xs text-green-600 font-semibold mt-1">
                        ✓ Completado
                      </p>
                    )}
                    <p className="text-xs font-mono text-slate-500 mt-1">
                      {checkpoint.latitude.toFixed(6)}, {checkpoint.longitude.toFixed(6)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Radio: {checkpoint.radius_feet} pies
                    </p>
                  </div>
                </Popup>
              </Marker>
            </div>
          );
        })}
        
        {isValidPoint(guardPosition) && (
          <Marker
            position={[guardPosition.latitude, guardPosition.longitude]}
            icon={guardIcon}
          >
            <Popup>
              <div className="p-2">
                <h3 className="font-bold text-orange-600">Tu Ubicación</h3>
                <p className="text-xs font-mono text-slate-500">
                  {guardPosition.latitude.toFixed(6)}, {guardPosition.longitude.toFixed(6)}
                </p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};
